/* global debounce */

/**
 * <typesense-search>
 *
 * Fullscreen header search overlay powered by Typesense `multi_search` (plain fetch, no libs).
 * - Federated: searches products + collections in one request; collections split by `is_brand`
 *   into Merken (brands) and Collecties (collections) tabs, alongside Producten.
 * - Empty state: popular searches + recently viewed products (localStorage `cc-recently-viewed`).
 * - Tabs reuse the existing <tabbed-content> element (tabs.js) and predictive-search CSS classes.
 * - Graceful fallback: misconfigured or failing requests close the overlay; the form still submits
 *   natively to /search.
 */
if (!customElements.get('typesense-search')) {
  const formatMoney = (amount, format) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
    const cents = Math.round(amount * 100);
    const fmt = format || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    const match = fmt.match(placeholder);
    const token = match ? match[1] : 'amount';
    let value;
    switch (token) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  };

  const esc = (str) => String(str == null ? '' : str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  window.TypesenseTheme = window.TypesenseTheme || {};
  window.TypesenseTheme.formatMoney = formatMoney;
  window.TypesenseTheme.esc = esc;

  const renderStars = (rating, count) => {
    if (!rating || rating <= 0) return '';
    const full = Math.round(rating);
    let s = '';
    for (let i = 1; i <= 5; i += 1) s += `<span class="ts-star${i <= full ? ' ts-star--on' : ''}">★</span>`;
    const c = count ? ` <span class="ts-rating__count">(${count})</span>` : '';
    return `<div class="ts-rating" aria-label="${rating} / 5">${s}${c}</div>`;
  };

  // Colour swatch dots — solid hex fill (or split gradient for multi-colour names),
  // image fallback. Visual only; the whole card links to the representative product.
  const swatchStyle = (hex) => {
    const parts = String(hex || '').match(/#[0-9a-fA-F]{3,8}/g) || [];
    if (parts.length >= 2) return `background:linear-gradient(135deg, ${parts[0]} 50%, ${parts[1]} 50%)`;
    if (parts.length === 1) return `background-color:${parts[0]}`;
    return '';
  };
  const swatchDots = (d) => {
    const hexes = Array.isArray(d.swatch_hexes) ? d.swatch_hexes : [];
    const imgs = Array.isArray(d.swatch_images) ? d.swatch_images : [];
    const titles = Array.isArray(d.swatch_titles) ? d.swatch_titles : [];
    const total = Math.max(hexes.length, imgs.length);
    if (total < 2) return '';
    const max = 7;
    let html = '';
    let shown = 0;
    for (let i = 0; i < total && shown < max; i += 1) {
      const t = titles[i] || '';
      const style = swatchStyle(hexes[i]);
      let dot = '';
      if (style) dot = `<span class="ts-swatch" title="${esc(t)}" style="${style}"></span>`;
      else if (imgs[i]) dot = `<span class="ts-swatch ts-swatch--img" title="${esc(t)}" style="background-image:url(${esc(imgs[i])})"></span>`;
      if (dot) { html += dot; shown += 1; }
    }
    const remaining = total - shown;
    const more = remaining > 0 ? `<span class="ts-swatch__more">+${remaining}</span>` : '';
    return html ? `<span class="ts-swatches">${html}${more}</span>` : '';
  };
  // Native-style product labels (custom / preorder / sale / new), reusing the theme's
  // .product-label classes so they look identical to the real product cards.
  const productLabels = (d) => {
    const cfg = (window.theme && window.theme.typesense) || {};
    const L = cfg.labels || {};
    const s = L.strings || {};
    const out = [];
    if (L.showCustom && d.label) out.push({ cls: 'custom', text: d.label });
    if (d.template_suffix && /preorder/i.test(d.template_suffix)) out.push({ cls: 'preorder', text: s.preorder || 'Pre-order' });
    if (L.showSale && typeof d.price === 'number' && typeof d.compare_at_price === 'number' && d.compare_at_price > d.price) {
      let text = s.sale || 'Sale';
      if (L.saleType === 'percent') text = (s.percent || '[amount]% off').replace('[amount]', Math.round((1 - (d.price / d.compare_at_price)) * 100));
      out.push({ cls: 'sale', text });
    }
    if (L.showNew) {
      let isNew = false;
      if (L.newViaTag && L.newTag && Array.isArray(d.tags)) {
        const t = String(L.newTag).toLowerCase();
        isNew = d.tags.some((x) => String(x).toLowerCase() === t);
      }
      if (!isNew && L.newViaDays && d.created_at) {
        isNew = ((Date.now() / 1000) - d.created_at) / 86400 < (L.newDays || 30);
      }
      if (isNew) out.push({ cls: 'new', text: s.new || 'New' });
    }
    const limited = out.slice(0, L.limit || 2);
    if (!limited.length) return '';
    const pos = L.position || 'top-0 start';
    const inner = limited.map((l) => `<div class="product-label-wrapper flex"><span class="product-label product-label--${l.cls}">${esc(l.text)}</span></div>`).join('');
    return `<div class="product-label-container absolute ${pos}">${inner}</div>`;
  };

  window.TypesenseTheme = window.TypesenseTheme || {};
  window.TypesenseTheme.swatchDots = swatchDots;
  window.TypesenseTheme.productLabels = productLabels;

  class TypesenseSearch extends HTMLElement {
    constructor() {
      super();
      this.cfg = (window.theme && window.theme.typesense) || {};
      this.strings = this.cfg.strings || {};
      this.blend = Boolean(window.theme && window.theme.settings && window.theme.settings.blendProductImages);
      this.popular = null; // resolved popular searches (data-driven, with fallback)
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.form = this.querySelector('.search');
      this.results = this.querySelector('.js-search-results');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.statusEl = this.querySelector('.js-search-status');
      this.legacyOverlay = this.querySelector('.js-search-overlay');
      this.loadingText = this.getAttribute('data-loading-text');
      this.abortController = null;

      this.enabled = Boolean(this.cfg.enabled && this.cfg.host && this.cfg.apiKey && this.input && this.form);
      if (this.enabled) this.build();
    }

    disconnectedCallback() {
      if (this.enabled) this.close();
    }

    /** Builds the overlay chrome by wrapping the existing form + results in a panel. */
    build() {
      if (this.legacyOverlay) this.legacyOverlay.remove();

      this.panel = document.createElement('div');
      this.panel.className = 'ts-search-panel';
      const bar = document.createElement('div');
      bar.className = 'ts-search-panel__bar';
      const body = document.createElement('div');
      body.className = 'ts-search-panel__body';

      this.closeBtn = document.createElement('button');
      this.closeBtn.type = 'button';
      this.closeBtn.className = 'ts-search-panel__close js-ts-close link';
      this.closeBtn.textContent = this.strings.close || 'Close';
      this.closeBtn.setAttribute('aria-label', this.strings.closeSearch || this.strings.close || 'Close search');

      bar.appendChild(this.form);
      bar.appendChild(this.closeBtn);
      body.appendChild(this.results);
      this.panel.appendChild(bar);
      this.panel.appendChild(body);
      this.appendChild(this.panel);

      this.addListeners();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('click', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.closeBtn.addEventListener('click', () => this.close());
      // Backdrop click (on the element itself, outside the panel) closes.
      this.addEventListener('mousedown', (evt) => {
        if (evt.target === this) this.close();
      });
      this.results.addEventListener('click', (evt) => {
        // "Show all" toggle on a compact mobile brand/category block.
        const more = evt.target.closest && evt.target.closest('.js-ts-more');
        if (more) {
          evt.preventDefault();
          const block = more.closest('.ts-side__block');
          const expanded = block.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', String(expanded));
          more.textContent = expanded
            ? (this.strings.showLess || 'Show less')
            : (this.strings.viewAllShort || this.strings.viewAll || 'View all');
          return;
        }
        // Brand/category chip → run that term as an inline search (instead of
        // navigating to an often-messy collection page).
        const term = evt.target.closest && evt.target.closest('.js-ts-term');
        if (term) {
          evt.preventDefault();
          const q = term.getAttribute('data-term') || '';
          this.input.value = q;
          this.input.setAttribute('value', q);
          this.input.focus();
          this.getResults(q);
          return;
        }
        // Search-result click → fire a Typesense analytics beacon (self-learning ranking).
        const card = evt.target.closest && evt.target.closest('.ts-card[data-id]');
        if (card) this.sendClick(card.getAttribute('data-id'));
      });

      // Remember which header control opened the search, so focus can return there on close.
      this.opener = null;
      document.querySelectorAll('.js-show-search, .js-show-search-left').forEach((btn) => {
        btn.addEventListener('click', () => { this.opener = btn; });
      });
    }

    /** Stable anonymous id so click analytics aren't dominated by one user. */
    getUserId() {
      try {
        let id = localStorage.getItem('ts-uid');
        if (!id) { id = `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem('ts-uid', id); }
        return id;
      } catch (e) { return 'anon'; }
    }

    /** Fire-and-forget click beacon to the Worker proxy (no write key in the browser). */
    sendClick(docId) {
      if (!docId || !this.cfg.enableAnalytics || !this.cfg.analyticsUrl) return;
      try {
        const body = JSON.stringify({ doc_id: docId, user_id: this.getUserId(), q: this.getQuery() });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(this.cfg.analyticsUrl, new Blob([body], { type: 'text/plain' }));
        } else {
          fetch(this.cfg.analyticsUrl, { method: 'POST', body, keepalive: true, mode: 'cors' });
        }
      } catch (e) { /* never block navigation */ }
    }

    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Builds product search parameters with proper field weighting and ranking.
     * sum_score makes query_by_weights actually drive the score; bucketed text-match
     * lets in-stock + best-sellers re-rank within each relevance tier.
     */
    /** Products shown in the overlay grid (the rest is behind the "view all" CTA). */
    overlayLimit() {
      const n = parseInt(this.cfg.overlayLimit, 10);
      return Number.isFinite(n) && n > 0 ? n : 8;
    }

    productParams() {
      const fields = [
        { f: 'title', w: 6, t: 2 },
        { f: 'vendor', w: 4, t: 2 },
        { f: 'product_type', w: 3, t: 2 },
        { f: 'subtitle', w: 2, t: 2 }
      ];
      if (this.cfg.includeSkus) fields.push({ f: 'sku', w: 2, t: 0 });
      if (this.cfg.includeTags) fields.push({ f: 'tags', w: 1, t: 2 });
      // Max 3 sort fields allowed by Typesense. available:true is already in filter_by,
      // so no _eval tier is needed. popularity_clicks is only added when analytics is on
      // (the field only exists once the Worker is set up).
      let sort = '_text_match(buckets:10):desc';
      if (this.cfg.rankBestsellers) sort += ',popularity:desc';
      if (this.cfg.rankBestsellers && this.cfg.enableAnalytics) sort += ',popularity_clicks:desc';
      const params = {
        query_by: fields.map((x) => x.f).join(','),
        query_by_weights: fields.map((x) => x.w).join(','),
        num_typos: fields.map((x) => x.t).join(','),
        text_match_type: 'sum_score',
        prioritize_token_position: true,
        sort_by: sort
      };
      if (this.cfg.groupVariants) {
        params.group_by = 'group_id';
        params.group_limit = 1;
      }
      return params;
    }

    handleSubmit(evt) {
      const redirect = this.redirectFor(this.getQuery());
      if (redirect) {
        evt.preventDefault();
        window.location.href = redirect;
        return;
      }
      const selected = this.querySelector('.ts-card[aria-selected="true"]');
      if (!this.getQuery().length || selected) {
        evt.preventDefault();
        if (selected) selected.click();
      }
      // else: native submit to /search
    }

    handleInput() {
      this.input.setAttribute('value', this.input.value);
      this.open();
      this.route(this.getQuery());
    }

    handleFocus() {
      if (this._suppressReopen) return; // focus was returned programmatically on close
      this.open();
      this.route(this.getQuery());
    }

    /** Routes a query to the right view: empty state, short-query nudge, or results. */
    route(term) {
      if (!term.length) this.renderEmptyState();
      else if (term.length === 1) this.renderShortQuery();
      else this.getResults(term);
    }

    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          this.navResults(evt.key);
          break;
        case 'Escape':
          this.close();
          break;
        case 'Tab':
          this.trapFocus(evt);
          break;
        // no default
      }
    }

    /** Keeps Tab focus inside the open dialog (wraps first ⇄ last). */
    trapFocus(evt) {
      if (!this.panel) return;
      const sel = 'a[href], button:not([disabled]), input:not([disabled]), speech-search-button, [tabindex]:not([tabindex="-1"])';
      const focusables = Array.prototype.filter.call(
        this.panel.querySelectorAll(sel),
        (el) => !el.hasAttribute('hidden') && el.offsetParent !== null
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    navResults(key) {
      const cards = this.querySelectorAll('.ts-card');
      if (!cards.length) return;
      const selected = this.querySelector('.ts-card[aria-selected="true"]');
      let idx = Array.prototype.indexOf.call(cards, selected);
      if (key === 'ArrowDown') idx = selected ? Math.min(idx + 1, cards.length - 1) : 0;
      else if (key === 'ArrowUp') { if (!selected) return; idx = Math.max(idx - 1, 0); }
      const toSelect = cards[idx];
      if (toSelect && toSelect !== selected) {
        toSelect.setAttribute('aria-selected', 'true');
        if (selected) selected.setAttribute('aria-selected', 'false');
        this.input.setAttribute('aria-activedescendant', toSelect.id || '');
        toSelect.scrollIntoView({ block: 'nearest' });
      }
    }

    /* ----------------------------------------------------------- requests */

    async tsMultiSearch(searches) {
      const { protocol, host, port, apiKey } = this.cfg;
      const url = `${protocol}://${host}:${port}/multi_search?x-typesense-api-key=${encodeURIComponent(apiKey)}`;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const controller = this.abortController;
      // Guard against a hung request: abort after 4s so the overlay can fall back.
      const timer = setTimeout(() => controller.abort(), 4000);
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify({ searches }),
          signal: controller.signal
        });
        if (!response.ok) throw new Error(response.status);
        return await response.json();
      } finally {
        clearTimeout(timer);
      }
    }

    async getResults(term) {
      this.setStatus(this.loadingText || this.strings.loading);
      const key = term.toLowerCase();
      if (this.cachedResults[key]) {
        this.renderResults(this.cachedResults[key], term);
        return;
      }
      this.renderSkeleton(); // compact placeholders so stale results aren't shown under a new query
      try {
        const data = await this.tsMultiSearch([
          {
            collection: this.cfg.collection,
            q: term,
            ...this.productParams(),
            filter_by: 'available:true && published_at:>0',
            per_page: this.overlayLimit(),
            page: 1,
            highlight_full_fields: 'title'
          },
          {
            collection: this.cfg.collectionsCollection,
            q: term,
            query_by: 'title',
            per_page: 20
          },
          {
            collection: this.cfg.popularCollection || 'product_queries',
            q: term,
            query_by: 'q',
            sort_by: '_text_match:desc,count:desc',
            per_page: 6,
            enable_analytics: false
          }
        ]);
        const results = data.results || [];
        // Normalise: with group_by, Typesense returns grouped_hits (one group per model).
        const extract = (r) => {
          if (!r) return [];
          if (r.grouped_hits) return r.grouped_hits.map((g) => g.hits && g.hits[0]).filter(Boolean);
          return r.hits || [];
        };
        // Dedupe + normalise suggestions (trim, collapse double spaces, case-insensitive),
        // drop the active term itself, cap at 5 (CSS shows ~4 in one scroll row on mobile).
        const seenSugg = new Set();
        const termKey = term.trim().replace(/\s+/g, ' ').toLowerCase();
        const suggestions = [];
        for (const h of ((results[2] && results[2].hits) || [])) {
          const norm = String((h.document && h.document.q) || '').trim().replace(/\s+/g, ' ');
          const key = norm.toLowerCase();
          if (!norm || key === termKey || seenSugg.has(key)) continue;
          seenSugg.add(key);
          suggestions.push(norm);
          if (suggestions.length >= 5) break;
        }
        const grouped = {
          products: extract(results[0]),
          collections: extract(results[1]),
          suggestions,
          found: (results[0] && results[0].found) || 0
        };
        this.cachedResults[key] = grouped;
        this.renderResults(grouped, term);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderNoResults(term);
      }
    }

    popularChips(items) {
      return `<ul class="ts-chips">${items.map((p) => `<li><a class="ts-chip" href="${this.cfg.searchUrl}?q=${encodeURIComponent(p)}">${esc(p)}</a></li>`).join('')}</ul>`;
    }

    /** Compact placeholder grid shown while a query loads (same grid → no layout shift). */
    renderSkeleton() {
      const n = this.overlayLimit();
      const card = '<li class="ts-skel" aria-hidden="true"><span class="ts-skel__img"></span><span class="ts-skel__body"><span class="ts-skel__line"></span><span class="ts-skel__line ts-skel__line--sm"></span></span></li>';
      this.results.innerHTML = `<div class="ts-results"><div class="ts-results__main"><ul class="ts-grid" role="presentation">${card.repeat(n)}</ul></div></div>`;
    }

    /** Single-character queries are too broad — nudge instead of running a heavy query. */
    renderShortQuery() {
      const msg = this.strings.typeMore || 'Type one more letter for more specific products.';
      this.results.innerHTML = `<div class="ts-noresults"><p class="ts-noresults__msg">${esc(msg)}</p></div>`;
    }

    async renderEmptyState() {
      const popular = await this.ensurePopular();
      let recent = [];
      if (this.cfg.showRecent) recent = await this.fetchRecent();

      // With recent products: popular in the side rail, recent grid in the main area.
      // Without recent: popular takes the main area as large chips.
      let side = '';
      let main = '';
      if (recent.length) {
        if (popular.length) {
          side = `<aside class="ts-results__side"><div class="ts-side__block"><h3 class="ts-side__title">${esc(this.strings.popular || 'Popular searches')}</h3>${this.popularChips(popular)}</div></aside>`;
        }
        main = `<div class="ts-main__head"><h3 class="ts-main__title">${esc(this.strings.recent || 'Recently viewed')}</h3></div>
          <ul class="ts-grid" role="list">${recent.map((h, i) => this.productCard(h, i)).join('')}</ul>`;
      } else if (popular.length) {
        main = `<div class="ts-main__head"><h3 class="ts-main__title">${esc(this.strings.popular || 'Popular searches')}</h3></div>${this.popularChips(popular)}`;
      }
      this.results.innerHTML = `<div class="ts-results"><div class="ts-results__main">${main}</div>${side}</div>`;
    }

    /**
     * Returns a redirect URL if the query exactly matches a configured
     * "term => /path" redirect (case-insensitive), else null.
     */
    redirectFor(term) {
      const t = (term || '').trim().toLowerCase();
      if (!t) return null;
      const lines = String(this.cfg.redirects || '').split(/\r?\n/);
      for (let i = 0; i < lines.length; i += 1) {
        const idx = lines[i].indexOf('=>');
        if (idx === -1) continue;
        const key = lines[i].slice(0, idx).trim().toLowerCase();
        const url = lines[i].slice(idx + 2).trim();
        if (key && url && key === t) return url;
      }
      return null;
    }

    /** Curated fallback list from the theme setting. */
    popularFallback() {
      return String(this.cfg.popularSearches || '')
        .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    }

    /**
     * Resolves popular searches data-driven from the `product_queries` analytics
     * collection (top by count), falling back to the curated setting when there
     * isn't enough data yet. Cached per overlay session.
     */
    async ensurePopular() {
      if (this.popular) return this.popular;
      const fallback = this.popularFallback();
      try {
        const { protocol, host, port, apiKey } = this.cfg;
        const url = `${protocol}://${host}:${port}/multi_search?x-typesense-api-key=${encodeURIComponent(apiKey)}`;
        const res = await fetch(url, {
          method: 'POST',
          body: JSON.stringify({
            searches: [{
              collection: this.cfg.popularCollection || 'product_queries',
              q: '*',
              query_by: 'q',
              sort_by: 'count:desc',
              per_page: 30,
              enable_analytics: false
            }]
          })
        });
        if (res.ok) {
          const data = await res.json();
          const hits = (data.results && data.results[0] && data.results[0].hits) || [];
          // Filter junk: drop the wildcard, very short partials, and duplicates.
          const clean = [];
          const seen = new Set();
          for (let i = 0; i < hits.length; i += 1) {
            const q = ((hits[i].document && hits[i].document.q) || '').trim();
            const key = q.toLowerCase();
            if (!q || q === '*' || q.length < 3 || seen.has(key)) continue;
            seen.add(key);
            clean.push(q);
            if (clean.length >= 6) break;
          }
          this.popular = clean.length >= 3 ? clean : fallback;
        } else {
          this.popular = fallback;
        }
      } catch (e) {
        this.popular = fallback;
      }
      return this.popular;
    }

    async fetchRecent() {
      let ids = [];
      try {
        ids = JSON.parse(localStorage.getItem('cc-recently-viewed') || '[]');
      } catch (e) { ids = []; }
      ids = ids.slice(0, 8).map(String);
      if (!ids.length) return [];
      try {
        const data = await this.tsMultiSearch([{
          collection: this.cfg.collection,
          q: '*',
          query_by: 'title',
          filter_by: `id:[${ids.join(',')}] && available:true && published_at:>0`,
          per_page: ids.length,
          enable_analytics: false
        }]);
        const hits = (data.results && data.results[0] && data.results[0].hits) || [];
        // Preserve recency order.
        const byId = {};
        hits.forEach((h) => { byId[h.document.id] = h; });
        return ids.map((id) => byId[id]).filter(Boolean);
      } catch (e) {
        return [];
      }
    }

    /* ----------------------------------------------------------- rendering */

    renderResults(grouped, term) {
      const products = grouped.products || [];
      const cols = grouped.collections || [];
      const brands = cols.filter((h) => h.document.is_brand);
      const collections = cols.filter((h) => !h.document.is_brand);

      if (!products.length && !brands.length && !collections.length) {
        this.renderNoResults(term, grouped.suggestions);
        return;
      }

      // Side rail: brands + collections only. Popular searches are EMPTY-STATE only
      // (never shown alongside results for an active query).
      const sideBlocks = [];
      if (brands.length) sideBlocks.push(this.sideBlock(this.strings.brands || 'Brands', brands, 6, 1));
      if (collections.length) sideBlocks.push(this.sideBlock(this.strings.collections || 'Collections', collections, 4, 3));
      const side = sideBlocks.length ? `<aside class="ts-results__side">${sideBlocks.join('')}</aside>` : '';

      // Main: natural result count ("717 resultaten") + compact "view all" header,
      // capped product grid, and a bottom "view all" CTA for the rest (no auto-load).
      const found = grouped.found || products.length;
      const countLabel = (this.strings.resultsCount || '[count] products')
        .replace('[count]', found).replace('{{ count }}', found);
      const viewAll = esc(this.strings.viewAll || 'View all results');
      const viewAllHref = `${this.cfg.searchUrl}?q=${encodeURIComponent(term)}`;
      const head = products.length
        ? `<div class="ts-main__head">
             <h3 class="ts-main__title">${esc(countLabel)}</h3>
             <a class="ts-main__viewall js-search-link" href="${viewAllHref}">${viewAll} <span aria-hidden="true">→</span></a>
           </div>`
        : '';
      const viewAllLabel = found > products.length ? `${viewAll} (${found})` : viewAll;
      const main = products.length
        ? `${head}
           <ul class="ts-grid" role="list">${products.map((h, i) => this.productCard(h, i)).join('')}</ul>
           <a class="ts-viewall has-ltr-icon" href="${viewAllHref}">${viewAllLabel} <span aria-hidden="true">→</span></a>`
        : `<p class="ts-main__empty">${(this.strings.noResults || 'No results found for "[terms]".').replace('[terms]', esc(term))}</p>`;

      const sugg = grouped.suggestions || [];
      const suggHtml = sugg.length
        ? `<ul class="ts-suggest" aria-label="${esc(this.strings.suggestions || 'Suggestions')}">${sugg.map((q) => `<li><a class="ts-suggest__item js-search-link" href="${this.cfg.searchUrl}?q=${encodeURIComponent(q)}" title="${esc(q)}">${esc(q)}</a></li>`).join('')}</ul>`
        : '';
      this.results.innerHTML = `${suggHtml}<div class="ts-results"><div class="ts-results__main">${main}</div>${side}</div>`;
    }

    productCard(hit, i) {
      const d = hit.document || {};
      const instant = this.cfg.preloadLinks ? ' data-instant' : '';
      const title = (hit.highlight && hit.highlight.title && hit.highlight.title.snippet)
        ? hit.highlight.title.snippet : esc(d.title);
      const mediaClass = `ts-card__media${this.blend ? ' image-blend' : ''}`;
      const media = d.image
        ? `<img class="img-fit" src="${esc(d.image)}" alt="${esc(d.title)}" loading="lazy">`
        : '';
      const onSale = typeof d.compare_at_price === 'number' && typeof d.price === 'number' && d.compare_at_price > d.price;
      const stars = d.rating ? renderStars(d.rating, d.rating_count) : '';
      let price = '';
      if (typeof d.price === 'number') {
        const cmp = onSale ? ` <s>${formatMoney(d.compare_at_price, this.cfg.moneyFormat)}</s>` : '';
        price = `<span class="ts-card__price${cmp ? ' ts-card__price--sale' : ''}">${formatMoney(d.price, this.cfg.moneyFormat)}${cmp}</span>`;
      }
      return `
        <li>
          <a class="ts-card js-search-link${d.available === false ? ' ts-card--sold-out' : ''}" id="tsearch-p-${i}" role="option" href="${esc(d.url)}" data-id="${esc(d.id)}"${instant}>
            <span class="${mediaClass}">${productLabels(d)}${media}</span>
            <span class="ts-card__body">
              <span class="ts-card__title">${title}</span>
              ${stars}
              ${price}
              ${swatchDots(d)}
            </span>
          </a>
        </li>`;
    }

    /**
     * Renders matched brands/categories as plain links to their collection page.
     * Deduplicates by title so doubles don't clutter the rail. Items without a
     * collection URL are skipped.
     */
    sideBlock(title, hits, max = 6, mobileCap = max) {
      const seen = new Set();
      const items = [];
      for (const h of hits) {
        const d = h.document || {};
        const label = (d.title || '').trim();
        const url = d.url;
        const key = label.toLowerCase();
        if (!label || !url || seen.has(key)) continue;
        seen.add(key);
        // Items beyond the mobile cap are hidden on mobile until "show all" is toggled
        // (CSS reveals them on desktop). Keeps brands/categories from crowding products.
        const extra = items.length >= mobileCap ? ' ts-side__item--extra' : '';
        items.push(`<li><a class="ts-side__item${extra} js-search-link" href="${esc(url)}">${esc(label)}</a></li>`);
        if (items.length >= max) break;
      }
      if (!items.length) return '';
      const more = items.length > mobileCap
        ? `<button type="button" class="ts-side__more js-ts-more" aria-expanded="false">${esc(this.strings.viewAllShort || this.strings.viewAll || 'View all')}</button>`
        : '';
      return `<div class="ts-side__block"><h3 class="ts-side__title">${esc(title)}</h3><ul class="ts-side__list" role="list">${items.join('')}</ul>${more}</div>`;
    }

    renderNoResults(term, suggestions) {
      const popular = this.popular || this.popularFallback();
      const popularHtml = popular.length
        ? `<div class="ts-noresults__pop"><h3 class="ts-side__title">${esc(this.strings.popular || 'Popular searches')}</h3>${this.popularChips(popular)}</div>`
        : '';
      const sugg = (suggestions || [])[0];
      const didYou = sugg
        ? `<p class="ts-noresults__didyou">${esc(this.strings.didYouMean || 'Did you mean')}: <a class="js-search-link" href="${this.cfg.searchUrl}?q=${encodeURIComponent(sugg)}">${esc(sugg)}</a>?</p>`
        : '';
      const msg = (this.strings.noResults || 'No results found for "[terms]".').replace('[terms]', esc(term));
      this.results.innerHTML = `<div class="ts-noresults"><p class="ts-noresults__msg">${msg}</p>${didYou}${popularHtml}</div>`;
    }

    setStatus(textValue) {
      if (!this.statusEl) return;
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = textValue || '';
      setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 1000);
    }

    /* ----------------------------------------------------------- open/close */

    open() {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      // Dialog semantics only while open (the form renders inline in the header when closed).
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('aria-label', this.strings.searchLabel || 'Search');
      this.ensurePopular(); // warm the data-driven popular searches
      this.input.setAttribute('aria-expanded', 'true');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      document.documentElement.classList.add('ts-search-open');

      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetHandler = this.resetHandler || (() => { this.input.value = ''; this.renderEmptyState(); this.input.focus(); });
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetHandler);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.panel.removeAttribute('aria-label');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.resetBtn.hidden = true;
      document.documentElement.classList.remove('ts-search-open');
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetHandler);
      this.returnFocus();
    }

    /**
     * Returns focus to the control that opened the overlay (header search icon), per
     * dialog a11y. Falls back to the search input — guarded so the open-on-focus
     * behaviour doesn't immediately re-open the overlay.
     */
    returnFocus() {
      const opener = this.opener;
      this.opener = null;
      if (opener && document.contains(opener) && opener.offsetParent !== null) {
        opener.focus();
        return;
      }
      this._suppressReopen = true;
      try { this.input.focus(); } catch (e) { /* input may be hidden when collapsed */ }
      setTimeout(() => { this._suppressReopen = false; }, 0);
    }
  }

  customElements.define('typesense-search', TypesenseSearch);
}
